import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { XCircle } from 'react-feather';
// Import Shared Components
import {
  Title,
  Alert,
  Table,
  Card,
  CardGroup,
  Search,
  Loader,
  Button,
  DatePicker,
  CSVButton,
  ButtonGroup,
} from 'shared-components';
// Import Shared Utils
import {
  getStartAndEndDates,
  getEndOfDay,
  getThirtyDaysAgo,
} from 'shared-components/src/utils/dates';
// Import Global Components
import ClientErrorComponent from '../../../components/ClientErrorComponent';
// Import Common Utils
import { getCardholderCSVData } from '../../../utils/csv';
import { formatDateParams } from '../../../utils/cardholders';
import { get } from '../../../utils/http';
// Import Hook
import { useTimeZone } from '../../../hooks/useTimeZone';
// Import filter options for search bar
import FILTER from './filter';
// Import CSV headers for cardholder list CSV generation
import { cardholderHeaders } from './csvHeaders';
// Import Component Styles
import {
  Header,
  DateAndButtonGroup,
  DatePickerRow,
  ClearDatesButton,
} from './styles';
// Import Columns for tables
import { getCardholderColumns } from './components/cardholderColumns';
import KYCTable from './components/KYCTable';

// role context
import {RoleContext} from '../../../context/RoleContext';

function Cardholders() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialPaginationState = {
    limit: 10,
    offset: 0,
    page: 0,
  };

  const initialDateState = {
    from: getThirtyDaysAgo(),
    to: getEndOfDay(),
  };

  // Set component states
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [kycEnabled, setKycEnabled] = useState(false);
  // Set data states
  const [pagination, setPagination] = useState(initialPaginationState);
  const [searchBy, setSearchBy] = useState(null);
  // Search value: PRN, External tag or accountID, kept when the filter changes
  const [searchValue, setSearchValue] = useState('');
  // Filters cardholders based on date range
  const [dateRange, setDateRange] = useState(initialDateState);
  // The applied filter driving fetches: a date range or a Search By term,
  // never both. 'none' makes no API call and shows an empty list.
  const [activeFilter, setActiveFilter] = useState({
    kind: 'date',
    range: initialDateState,
  });
  const [cardholders, setCardholders] = useState({ count: 0, data: [] });
  const [timeZone] = useTimeZone();

  // Get role from context
  const userRole = React.useContext(RoleContext);

  // Fetch initial data and fetch whenever the applied filter updates
  useEffect(() => {
    if (activeFilter.kind === 'none') {
      setCardholders({ count: 0, data: [] });
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      setFetching(true);
      try {
        // format date query string by passing the timezone and the local time of that timezone
        // Search By queries send no date params so matches are returned
        // regardless of time range
        const filterParams =
          activeFilter.kind === 'search'
            ? { [activeFilter.field]: activeFilter.value }
            : formatDateParams(activeFilter.range, timeZone);
        const query = new URLSearchParams({
          ...filterParams,
          ...pagination,
        }).toString();

        const cardholderListData = await get(
          `/api/v1/client_portal/card_issuing/cardholders?${query}`
        );
        const kycEnabledData = await get(
          '/api/v1/client_portal/card_issuing/programs/kyc_config'
        );
        const companyKycEnabled = kycEnabledData.data.some(
          kyc_config => kyc_config.kyc_enabled
        );
        if (companyKycEnabled) {
          setKycEnabled(true);
        }
        setCardholders(cardholderListData);
      } catch (e) {
        setError(true);
      }
      setFetching(false);
      setLoading(false);
    };
    fetchData();
  }, [activeFilter, pagination, timeZone]);

  // Display success message on page if required
  useEffect(() => {
    if (location.state && location.state.success) {
      setSuccess(location.state.success);
      // Remove success message from state

      const clearSuccess = setTimeout(() => {
        // Clear Success message after 5 seconds
        setSuccess(null);
      }, 5000);
      // clean up timer before useEffect and when component unmounts
      return () => {
        setSuccess(null);
        clearTimeout(clearSuccess);
      };
    }

    return () => setSuccess(null);
  }, [location.state]);

  const isSearchValid = Boolean(searchBy && searchValue.trim());

  // Triggers a cardholder search on the selected Search By filter only,
  // so matches are returned regardless of any time range
  const searchCardholders = () => {
    if (!isSearchValid || fetching) return;
    setActiveFilter({
      kind: 'search',
      field: searchBy,
      value: searchValue.trim(),
    });
    // Reset initial pagination params
    setPagination(initialPaginationState);
  };

  // Search By and the date range are mutually exclusive: picking a filter
  // clears the dates, and results clear until a search runs
  const handleSearchByChange = field => {
    const nextSearchBy = field || null;
    // Re-selecting the current filter is a no-op so shown results aren't cleared
    if (nextSearchBy === searchBy) return;
    setSearchBy(nextSearchBy);
    // Switching to a different filter clears the typed search value
    setSearchValue('');
    setDateRange({ from: '', to: '' });
    setActiveFilter({ kind: 'none' });
    setPagination(initialPaginationState);
  };

  // Selecting a date range clears the Search By selection and typed value,
  // then triggers a search
  const handleDatesChange = ({ startDate, endDate }) => {
    setSearchBy(null);
    setSearchValue('');
    if (!startDate && !endDate) {
      setDateRange({ from: '', to: '' });
      setActiveFilter({ kind: 'none' });
      setPagination(initialPaginationState);
      return;
    }
    // Normalize empty values to undefined: getStartAndEndDates only
    // falls back on undefined, and '' would become an Invalid Date
    const { validStartDate, validEndDate } = getStartAndEndDates(
      startDate || undefined,
      endDate || undefined
    );
    const range = { from: validStartDate, to: validEndDate };
    setDateRange(range);
    setActiveFilter({ kind: 'date', range });
    setPagination(initialPaginationState);
  };

  const hasDates = Boolean(dateRange.from || dateRange.to);

  // Clears the date range; with no filter left active, results clear too
  const clearDates = () => {
    setDateRange({ from: '', to: '' });
    setActiveFilter({ kind: 'none' });
    setPagination(initialPaginationState);
  };

  // Handle change in table page size
  const handlePageSizeChange = (pageSize, pageIndex) => {
    setPagination({
      ...pagination,
      limit: pageSize,
      offset: pageIndex * pageSize,
    });
  };

  const handlePageChange = pageIndex => {
    setPagination({
      ...pagination,
      page: pageIndex,
      offset: pageIndex * pagination.limit,
    });
  };

  if (loading) return <Loader />;
  if (error) return <ClientErrorComponent data={{}} />;

  return (
    <CardGroup>
      {success && <Alert type="success">{success}</Alert>}
      <Header>
        <Card>
          <Search
            searchOptions={FILTER}
            updateSearchBy={handleSearchByChange}
            searchBy={searchBy}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') searchCardholders();
            }}
            onClick={() => searchCardholders()}
            searchDisabled={!isSearchValid || fetching}
          />
        </Card>
        <DateAndButtonGroup>
          <Card>
            <DatePickerRow>
              <DatePicker
                startDate={dateRange.from}
                endDate={dateRange.to}
                onDatesChange={handleDatesChange}
                isOutsideRange={() => false}
                hideKeyboardShortcutsPanel
                showDefaultInputIcon
              />
              <ClearDatesButton
                type="button"
                aria-label="Clear date range"
                title="Clear date range"
                onClick={clearDates}
                disabled={!hasDates}
              >
                <XCircle size={18} />
              </ClearDatesButton>
            </DatePickerRow>
          </Card>
          <Card>
            <ButtonGroup>
              <CSVButton
                data={getCardholderCSVData(cardholders.data)}
                // Forces react-CSV component unmount and remount whenever cardholder updates
                // Due to a bug in react-CSV
                key={cardholders.data.map(ch => ch.id).join('')}
                headers={cardholderHeaders}
                filename={`cardholders-${new Date().toLocaleDateString()}`}
              >
                Export to CSV
              </CSVButton>
              <Button
                type="primary"
                disabled={userRole === 'user' }
                onClick={() => navigate('/card-issuing/cardholders/create')}
              >
                Create Cardholders
              </Button>
            </ButtonGroup>
          </Card>
        </DateAndButtonGroup>
      </Header>
      <Card>
        <Title>Cardholder List</Title>
        <Table
          columns={getCardholderColumns(timeZone)}
          data={cardholders.data}
          noDataText="Currently there are no Cardholders"
          /* eslint-disable no-unused-vars */
          getTrProps={(state, rowInfo, column) => ({
            style: {
              cursor: 'pointer',
            },
            className: 'hoverable',
            onClick: (e, handleOriginal) => {
              navigate(
                `/card-issuing/cardholders/${rowInfo.original.id}`
              );
            },
          })}
          showPagination={cardholders.count > pagination.limit}
          pages={Math.ceil(cardholders.count / pagination.limit)}
          page={pagination.page}
          manual
          onPageSizeChange={handlePageSizeChange}
          onPageChange={handlePageChange}
          /* eslint-enable no-unused-vars */
          sortable={false}
          defaultSorted={[{ id: 'id', desc: true }]}
        />
      </Card>
      {kycEnabled && <KYCTable />}
    </CardGroup>
  );
}

export default Cardholders;